import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/shared/Layout';
import { Card, PageHeader, Button, Badge } from '../../components/shared/ui';
import { studentAPI } from '../../utils/api';
import api from '../../utils/api';
import { calcProfileProgress } from '../../utils/profileProgress';
import useAuthStore from '../../store/authStore';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer
} from 'recharts';

const STATUS_COLORS = { pending:'#94a3b8', shortlisted:'#f59e0b', invited:'#3b82f6', accepted:'#22c55e', rejected:'#ef4444' };
const STATUS_LABELS = { pending:'Bekliyor', shortlisted:'Ön Liste', invited:'Davet', accepted:'Kabul', rejected:'Red' };

const StatCard = ({ label, value, tone='blue' }) => {
  const tones = { blue:'text-brand-700', green:'text-emerald-600', amber:'text-amber-600', violet:'text-violet-600' };
  return (
    <Card>
      <div className={`text-2xl sm:text-3xl font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-xs sm:text-sm text-ink-500 mt-1">{label}</div>
    </Card>
  );
};

const StudentDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [showPrefModal, setShowPrefModal] = useState(false);

  useEffect(() => {
    studentAPI.getProfile().then(r => {
      setProfile(r.data);
      const hasType = r.data?.internship_preferences?.internship_type;
      if (!hasType && !sessionStorage.getItem('stajPrefModalSeen')) setShowPrefModal(true);
    });
    api.get('/applications/mine').then(r => setApplications(r.data)).catch(() => {});
  }, []);

  const dismissPrefModal = () => { sessionStorage.setItem('stajPrefModalSeen', '1'); setShowPrefModal(false); };
  const goToPrefs = () => { sessionStorage.setItem('stajPrefModalSeen', '1'); navigate('/student/profile?section=preferences'); };

  const completionPct = calcProfileProgress(profile);

  const invitations = applications.filter(a => a.status === 'invited');

  const statusData = Object.entries(
    applications.reduce((acc, a) => { acc[a.status] = (acc[a.status]||0)+1; return acc; }, {})
  ).map(([status, count]) => ({ name: STATUS_LABELS[status] || status, value: count, status }));

  return (
    <Layout>
      <PageHeader
        title={`Hoş geldin${profile?.first_name ? ', '+profile.first_name : ''}!`}
        description={user?.email}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Profil"   value={`%${completionPct}`} tone={completionPct === 100 ? 'green' : 'amber'} />
        <StatCard label="Davetler" value={invitations.length} tone="blue" />
        <StatCard label="Kabul"    value={applications.filter(a => a.status === 'accepted').length} tone="green" />
        <StatCard label="Toplam"   value={applications.length} tone="violet" />
      </div>

      {completionPct < 100 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="font-medium text-amber-900">Profilin eksik (%{completionPct})</div>
              <div className="text-sm text-amber-800 mt-0.5">Şirketler seni bulamaz — profilini tamamla, CV yükle.</div>
            </div>
            <Button as={Link} to="/student/profile" className="!bg-amber-600 hover:!bg-amber-700">Tamamla</Button>
          </div>
        </Card>
      )}

      {applications.length > 0 && (
        <Card className="mb-6">
          <h3 className="text-sm font-semibold text-ink-900 mb-3">Başvuru Durumları</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({name,value})=>`${name}: ${value}`} labelLine={false} fontSize={11}>
                {statusData.map(e => <Cell key={e.status} fill={STATUS_COLORS[e.status] || '#94a3b8'} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-ink-900">Davetlerim</h2>
          <Link to="/student/applications" className="text-sm text-brand-700 hover:text-brand-800">Tümünü gör →</Link>
        </div>
        {invitations.slice(0,5).map(a => (
          <div key={a.id} className="flex items-center justify-between py-3 border-b border-ink-100 last:border-0 gap-3">
            <div className="min-w-0">
              <div className="font-medium text-sm text-ink-900 truncate">{a.position_title}</div>
              <div className="text-xs text-ink-500 truncate">{a.company_name}</div>
            </div>
            <Badge tone="blue">Davet</Badge>
          </div>
        ))}
        {invitations.length === 0 && (
          <p className="text-ink-400 text-sm text-center py-4">
            Henüz davet yok. Şirketler profilini incelediğinde seni davet eder.
          </p>
        )}
      </Card>

      {showPrefModal && (
        <div
          onClick={dismissPrefModal}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, maxWidth: 420, width: '100%', padding: '28px 26px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center' }}
          >
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>Staj tercihini belirt</h3>
            <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6, margin: '0 0 22px' }}>
              Şirketlerin seni doğru ilanlarda görebilmesi için <b>staj türünü</b> (Zorunlu / Gönüllü) ve tercihlerini seçer misin? Sadece birkaç saniye sürer.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={goToPrefs} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Şimdi seç</button>
              <button onClick={dismissPrefModal} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Sonra</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentDashboard;
